import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Paper, Stack, Table, TableBody,
  TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { useToast } from '../../context/ToastContext';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';
import {
  GatewayRefund, GatewayRefundService, RefundAttempt, RefundEligibility, RefundProvider, RefundStatus,
} from '../../services/gatewayRefund.service';
import ScrollableTable from '../common/ScrollableTable';
import { formatMoney, humanizeEnum } from '../../utils/format';
import { formatDate, formatDateTime, toLocalIsoDate } from '../../utils/dateUtils';
import { AccountService } from '../../services/account.service';
import { Account } from '../../interfaces/accounting.interface';
import { AccountAutocomplete } from '../common/AccountAutocomplete';
import { depositAccountOptions } from '../../utils/postingAccounts';

// ---- P1-B gateway refunds ----
export const PROVIDER_LABEL: Record<RefundProvider, string> = { BKASH: 'bKash', SSLCOMMERZ: 'SSLCommerz' };
const STATUS_COLOR: Record<RefundStatus, 'success' | 'warning' | 'error' | 'default' | 'info'> = {
  REQUESTED: 'info', PROCESSING: 'info', COMPLETED: 'success', FAILED: 'error', NEEDS_RECONCILIATION: 'warning', REVERSED: 'default',
};

const latestFirst = (a: RefundAttempt, b: RefundAttempt) => (b.paidAt ?? '').localeCompare(a.paidAt ?? '');

const CreditNoteGatewayRefund: React.FC<{ noteId: string; onChanged?: () => void }> = ({ noteId, onChanged }) => {
  const toast = useToast();
  const canRefund = usePermission(PERMISSIONS.GATEWAY_REFUND_MANAGE);
  const [elig, setElig] = useState<RefundEligibility | null>(null);
  const [refunds, setRefunds] = useState<GatewayRefund[]>([]);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [attemptId, setAttemptId] = useState('');
  const [amount, setAmount] = useState<number | ''>('');
  const [reason, setReason] = useState('');
  const [resolving, setResolving] = useState<GatewayRefund | null>(null);
  const [resolveNote, setResolveNote] = useState('');
  // fix round: "Book by hand" books a manual refund that consumes the parked refund's reserve, then resolves it
  const [manual, setManual] = useState<GatewayRefund | null>(null);
  const [manualAccounts, setManualAccounts] = useState<Account[]>([]);
  const [manualAccount, setManualAccount] = useState('');
  const [manualRef, setManualRef] = useState('');
  const [manualNote, setManualNote] = useState('');
  const openManual = async (r: GatewayRefund) => {
    try {
      setManualAccounts(depositAccountOptions(await AccountService.getAccountsByType('ASSET')));
    } catch (e) {
      toast.showApiError(e, 'Failed to load accounts');
    }
    setManualAccount(''); setManualRef(''); setManualNote(''); setManual(r);
  };

  const load = useCallback(async () => {
    try {
      const [e, r] = await Promise.all([GatewayRefundService.eligibility(noteId), GatewayRefundService.list(noteId)]);
      setElig(e); setRefunds(Array.isArray(r) ? r : []);
    } catch {
      setElig(null); setRefunds([]); // the refund block simply stays hidden when the lookup fails
    }
  }, [noteId]);
  useEffect(() => { if (canRefund) load(); }, [canRefund, load]);

  const eligible = useMemo(() => (elig?.attempts ?? []).filter(a => a.eligible).sort(latestFirst), [elig]);
  const attempt = eligible.find(a => a.attemptId === attemptId);
  const capFor = (a?: RefundAttempt) => (elig && a ? Math.min(elig.cap, a.refundable) : 0);
  const tooBig = amount !== '' && Number(amount) > capFor(attempt) + 0.005;

  const openDialog = () => {
    const first = eligible[0];
    setAttemptId(first?.attemptId ?? ''); setAmount(capFor(first)); setReason(''); setOpen(true);
  };
  const pick = (id: string) => { setAttemptId(id); setAmount(capFor(eligible.find(a => a.attemptId === id))); };

  const run = async (fn: () => Promise<unknown>, ok: string, fail: string) => {
    setBusy(true);
    try { await fn(); toast.showSuccess(ok); await load(); onChanged?.(); }
    catch (e) { toast.showApiError(e, fail); }
    finally { setBusy(false); }
  };

  if (!canRefund || !elig) return null;
  if (!elig.eligible && refunds.length === 0 && !elig.reason) return null;
  const providers = Array.from(new Set(eligible.map(a => PROVIDER_LABEL[a.provider]))).join(' / ');

  return (
    <Paper sx={{ p: 2, mt: 3 }} data-help-section="sales.creditNote.gatewayRefund">
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1 }}>
        <Typography variant="h6">Gateway refunds</Typography>
        {elig.eligible && eligible.length > 0 && (
          <Button variant="outlined" disabled={busy} data-help="sales.creditNote.gatewayRefund.request" onClick={openDialog}>Refund via {providers}</Button>
        )}
      </Box>
      {!elig.eligible && elig.reason && <Typography variant="body2" color="text.secondary">{elig.reason}</Typography>}
      {refunds.length > 0 && (
        <ScrollableTable>
          <Table size="small">
            <TableHead><TableRow>
              <TableCell data-help="sales.creditNote.gatewayRefund.col.date">Date</TableCell><TableCell data-help="sales.creditNote.gatewayRefund.col.gateway">Gateway</TableCell><TableCell align="right" data-help="sales.creditNote.gatewayRefund.col.amount">Amount</TableCell>
              <TableCell data-help="sales.creditNote.gatewayRefund.col.status">Status</TableCell><TableCell data-help="sales.creditNote.gatewayRefund.col.reason">Reason</TableCell><TableCell />
            </TableRow></TableHead>
            <TableBody>
              {refunds.map(r => (
                <TableRow key={r.id}>
                  <TableCell>{r.createdAt ? formatDate(r.createdAt) : ''}</TableCell>
                  <TableCell>{PROVIDER_LABEL[r.provider] ?? r.provider}{r.mode === 'SANDBOX' ? ' (sandbox)' : ''}</TableCell>
                  <TableCell align="right">{formatMoney(r.amount)}</TableCell>
                  <TableCell>
                    <Chip size="small" label={humanizeEnum(r.status)} color={STATUS_COLOR[r.status] ?? 'default'} />
                    {r.failureReason && <Typography variant="caption" display="block" color="text.secondary">{r.failureReason}</Typography>}
                  </TableCell>
                  <TableCell>{r.reason}</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    {(r.status === 'PROCESSING' || r.status === 'NEEDS_RECONCILIATION' || r.status === 'REQUESTED') && (
                      <Button size="small" disabled={busy} data-help="sales.creditNote.gatewayRefund.action.recheck" onClick={() => run(() => GatewayRefundService.recheck(r.id), 'Status re-checked', 'Failed to re-check the refund')}>Re-check</Button>
                    )}
                    {(r.status === 'NEEDS_RECONCILIATION' || r.status === 'REQUESTED') && !r.creditNoteRefundId && (
                      <>
                        {/* money that left through the gateway is never resolved without booking it */}
                        {!r.providerRefundId && (
                          <Button size="small" disabled={busy} data-help="sales.creditNote.gatewayRefund.action.resolve" onClick={() => { setResolveNote(''); setResolving(r); }}>Mark resolved</Button>
                        )}
                        {r.status === 'NEEDS_RECONCILIATION' && r.providerRefundId && (
                          <Button size="small" disabled={busy} data-help="sales.creditNote.gatewayRefund.action.book" onClick={() => run(() => GatewayRefundService.book(r.id), 'Refund booked', 'Failed to book the refund')}>Book now</Button>
                        )}
                        <Button size="small" disabled={busy} data-help="sales.creditNote.gatewayRefund.action.manual" onClick={() => openManual(r)}>Book by hand</Button>
                      </>
                    )}
                    {r.status === 'NEEDS_RECONCILIATION' && r.creditNoteRefundId && (
                      <Button size="small" disabled={busy} data-help="sales.creditNote.gatewayRefund.action.resolve" onClick={() => { setResolveNote(''); setResolving(r); }}>Mark resolved</Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </ScrollableTable>
      )}

      {open && (
        <Dialog open onClose={() => !busy && setOpen(false)} maxWidth="sm" fullWidth data-help-page="sales.creditNote.gatewayRefund.request.page">
          <DialogTitle>Refund through the gateway</DialogTitle>
          <DialogContent data-help-section="sales.creditNote.gatewayRefund.request.section.form">
            <Stack spacing={2} sx={{ pt: 1 }}>
              {attempt?.mode === 'SANDBOX' && (
                <Alert severity="warning">SANDBOX payment: no real money moves, but the refund is booked in the books like a real one.</Alert>
              )}
              <TextField select data-help="sales.creditNote.gatewayRefund.request.payment" label="Payment to refund" fullWidth value={attemptId} onChange={e => pick(e.target.value)}>
                {eligible.map(a => (
                  <MenuItem key={a.attemptId} value={a.attemptId}>
                    {PROVIDER_LABEL[a.provider]} {a.mode === 'SANDBOX' ? '(sandbox) ' : ''}- {a.paidAt ? formatDateTime(a.paidAt) : ''} - paid {formatMoney(a.amount)}, refundable {formatMoney(a.refundable)}
                  </MenuItem>
                ))}
              </TextField>
              <TextField data-help="sales.creditNote.gatewayRefund.request.amount" label="Amount" type="number" required fullWidth value={amount} error={tooBig}
                helperText={tooBig ? `More than the maximum ${formatMoney(capFor(attempt))}` : `Up to ${formatMoney(capFor(attempt))}`}
                onChange={e => setAmount(e.target.value === '' ? '' : Number(e.target.value))} inputProps={{ min: 0, step: '0.01' }} />
              <TextField data-help="sales.creditNote.gatewayRefund.request.reason" label="Reason" required fullWidth multiline minRows={2} value={reason} inputProps={{ maxLength: 255 }}
                onChange={e => setReason(e.target.value)} />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button data-help="sales.creditNote.gatewayRefund.request.cancel" onClick={() => setOpen(false)} disabled={busy}>Cancel</Button>
            <Button variant="contained" data-help="sales.creditNote.gatewayRefund.request.confirm" disabled={busy || !attempt || amount === '' || Number(amount) <= 0 || tooBig || !reason.trim()}
              onClick={async () => {
                await run(() => GatewayRefundService.request(noteId, { attemptId, amount: Number(amount), reason: reason.trim() }),
                  'Refund requested', 'Failed to request the refund');
                setOpen(false);
              }}>Refund</Button>
          </DialogActions>
        </Dialog>
      )}
      {manual && (
        <Dialog open onClose={() => !busy && setManual(null)} maxWidth="sm" fullWidth data-help-page="sales.creditNote.gatewayRefund.manual.page">
          <DialogTitle>Book refund by hand</DialogTitle>
          <DialogContent data-help-section="sales.creditNote.gatewayRefund.manual.section.form">
            <Stack spacing={2} sx={{ pt: 1 }}>
              <Typography variant="body2">
                Records a refund of {formatMoney(manual.amount)} paid from the account you choose, using the amount this gateway
                refund holds, and closes the gateway refund.
                {manual.providerRefundId ? ` ${PROVIDER_LABEL[manual.provider] ?? manual.provider} reference: ${manual.providerRefundId}.` : ''}
              </Typography>
              <AccountAutocomplete data-help="sales.creditNote.gatewayRefund.manual.account" accounts={manualAccounts} value={manualAccount} onChange={setManualAccount}
                label="Paid from account" required />
              <TextField data-help="sales.creditNote.gatewayRefund.manual.reference" label="Reference" fullWidth value={manualRef} onChange={e => setManualRef(e.target.value)}
                inputProps={{ maxLength: 255 }} />
              <TextField data-help="sales.creditNote.gatewayRefund.manual.note" label="Note" required fullWidth multiline minRows={2} value={manualNote} inputProps={{ maxLength: 250 }}
                onChange={e => setManualNote(e.target.value)} />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button data-help="sales.creditNote.gatewayRefund.manual.cancel" onClick={() => setManual(null)} disabled={busy}>Cancel</Button>
            <Button variant="contained" data-help="sales.creditNote.gatewayRefund.manual.confirm" disabled={busy || !manualAccount || !manualNote.trim()} onClick={async () => {
              const r = manual;
              await run(() => GatewayRefundService.bookManually(r.id, {
                refundDate: toLocalIsoDate(), paymentAccountId: manualAccount,
                reference: manualRef.trim() || undefined, note: manualNote.trim(),
              }), 'Refund booked', 'Failed to book the refund');
              setManual(null);
            }}>Book</Button>
          </DialogActions>
        </Dialog>
      )}
      {resolving && (
        <Dialog open onClose={() => setResolving(null)} maxWidth="sm" fullWidth data-help-page="sales.creditNote.gatewayRefund.resolve.page">
          <DialogTitle>Mark refund resolved</DialogTitle>
          <DialogContent data-help-section="sales.creditNote.gatewayRefund.resolve.section.form">
            <Typography variant="body2" sx={{ mb: 2 }}>Use this when you have confirmed the outcome with the gateway outside the system.</Typography>
            <TextField data-help="sales.creditNote.gatewayRefund.resolve.note" label="Note" required fullWidth multiline minRows={2} value={resolveNote} inputProps={{ maxLength: 250 }}
              onChange={e => setResolveNote(e.target.value)} />
          </DialogContent>
          <DialogActions>
            <Button data-help="sales.creditNote.gatewayRefund.resolve.cancel" onClick={() => setResolving(null)}>Cancel</Button>
            <Button variant="contained" data-help="sales.creditNote.gatewayRefund.resolve.confirm" disabled={busy || !resolveNote.trim()} onClick={async () => {
              const r = resolving;
              await run(() => GatewayRefundService.resolve(r.id, { note: resolveNote.trim() }), 'Refund marked resolved', 'Failed to resolve the refund');
              setResolving(null);
            }}>Mark resolved</Button>
          </DialogActions>
        </Dialog>
      )}
    </Paper>
  );
};

export default CreditNoteGatewayRefund;
// ---- end P1-B ----
